import { Loader2Icon } from "lucide-react"
import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { useChangeConfigProcessor } from "@/services/admin/merchant-processors"
import type { MerchantProcessor } from "@/types/admin-merchant-processor"
import type { ProcessorConfigItem } from "@/types/admin-processor-config"

const prefix = "pages.adminProcessorConfig"

export interface PendingProcessorChange {
  item: ProcessorConfigItem
  processor: MerchantProcessor
}

interface ChangeProcessorDialogProps {
  /** `null` mantém o diálogo fechado. */
  pending: PendingProcessorChange | null
  onClose: () => void
}

/**
 * Confirmação da troca de credencial.
 *
 * A troca muda por onde o dinheiro sai, então nunca é silenciosa — e o diálogo diz de quem
 * para quem, para o operador reconhecer um clique errado antes de confirmá-lo.
 *
 * Componente compartilhado porque a troca acontece em dois lugares: na linha do painel e na
 * página do meio de pagamento. Duplicar o texto do alerta deixaria os dois avisos divergirem.
 */
export const ChangeProcessorDialog = ({
  pending,
  onClose,
}: ChangeProcessorDialogProps) => {
  const { t } = useTranslation()
  const { mutate, isPending } =
    useChangeConfigProcessor(onClose)

  return (
    <AlertDialog
      open={pending !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(`${prefix}.changeProcessor.title`)}
          </AlertDialogTitle>
          {/* Tudo o que o operador precisa ouvir vive DENTRO da description: o
              Radix aponta o aria-describedby só para ela, e o aviso de
              configuração ativa é a parte mais consequente do diálogo. */}
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                {t(
                  `${prefix}.changeProcessor.description`,
                  {
                    method: pending
                      ? t(
                          `${prefix}.methods.${pending.item.method}`,
                          {
                            defaultValue:
                              pending.item.method,
                          }
                        )
                      : "",
                    whiteLabel:
                      pending?.item.whiteLabelName ??
                      pending?.item.whiteLabelId ??
                      "",
                    from:
                      pending?.item.processor?.name ??
                      pending?.item.processor?.id ??
                      t(`${prefix}.processorUnresolved`),
                    to:
                      pending?.processor.name ??
                      pending?.processor.id ??
                      "",
                  }
                )}
              </p>
              {/* Configuração ativa roteia cobrança de verdade: o aviso não pode
                  ter o mesmo peso do de uma inativa. */}
              {pending?.item.active && (
                <span className="block rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                  {t(
                    `${prefix}.changeProcessor.activeWarning`
                  )}
                </span>
              )}
              <span className="block text-xs text-muted-foreground">
                {t(`${prefix}.changeProcessor.audited`)}
              </span>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>
            {t(`${prefix}.changeProcessor.cancel`)}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={isPending}
            onClick={(event) => {
              // Sem isto o Radix fecha o diálogo no clique, e o operador perde o
              // retorno de erro da requisição que acabou de disparar.
              event.preventDefault()
              if (pending) {
                mutate({
                  configId: pending.item.id,
                  processorId: pending.processor.id,
                })
              }
            }}
          >
            {isPending && (
              <Loader2Icon
                className="mr-2 size-4 animate-spin"
                aria-hidden="true"
              />
            )}
            {isPending
              ? t(`${prefix}.changeProcessor.confirming`)
              : t(`${prefix}.changeProcessor.confirm`)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
